import type { RunwayColor } from "../config/palette";

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export interface MutableVec2 {
  x: number;
  y: number;
}

export interface RunwayDefinition {
  readonly id: RunwayColor;
  readonly color: RunwayColor;
  /** Touchdown gate: aircraft must arrive here to land. */
  readonly approach: Vec2;
  /** Roll-out end of the runway, and the point departures lift off from. */
  readonly end: Vec2;
  readonly width: number;
}

export interface GroundRoute {
  /** Taxi path walked after landing, from the runway end to the apron. */
  readonly arrival: readonly Vec2[];
  /** Taxi path walked back out to the runway approach gate. */
  readonly departure: readonly Vec2[];
}

export type GrassShade = "light" | "mid" | "dark";

export interface GrassPatch {
  readonly shade: GrassShade;
  readonly points: readonly Vec2[];
}

export interface DirtField {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly depth: number;
}

export interface StreamDefinition {
  readonly width: number;
  readonly points: readonly Vec2[];
}

export interface TreeDefinition {
  readonly x: number;
  readonly y: number;
  readonly scale?: number;
}

export type BuildingKind = "terminal" | "hangar" | "tower";

export interface BuildingDefinition {
  readonly kind: BuildingKind;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly depth: number;
  readonly height: number;
  readonly rotation?: number;
}

export interface SceneryDefinition {
  readonly grassPatches: readonly GrassPatch[];
  readonly dirtFields: readonly DirtField[];
  readonly stream?: StreamDefinition;
  readonly trees: readonly TreeDefinition[];
  readonly buildings: readonly BuildingDefinition[];
}

export interface AirportMap {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly tagline: string;
  readonly runways: readonly RunwayDefinition[];
  readonly groundRoutes: Readonly<Record<RunwayColor, GroundRoute>>;
  readonly scenery: SceneryDefinition;
}

/** Axis-aligned bounds plus the four (possibly rotated) view corners. */
export interface WorldBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  readonly corners?: readonly Vec2[];
}

function isFinitePoint(point: Vec2): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}

export function validateAirportMap(map: AirportMap): AirportMap {
  if (map.runways.length === 0) {
    throw new Error("Airport must define at least one runway");
  }

  const seen = new Set<string>();
  for (const runway of map.runways) {
    if (seen.has(runway.id)) {
      throw new Error(`Duplicate runway ID: ${runway.id}`);
    }
    seen.add(runway.id);

    if (!isFinitePoint(runway.approach) || !isFinitePoint(runway.end)) {
      throw new Error(`Runway ${runway.id} has a non-finite endpoint`);
    }
    if (
      runway.approach.x === runway.end.x &&
      runway.approach.y === runway.end.y
    ) {
      throw new Error(`Runway ${runway.id} is zero-length`);
    }
    if (!Number.isFinite(runway.width) || runway.width <= 0) {
      throw new Error(`Runway ${runway.id} width must be positive`);
    }

    const routes = map.groundRoutes[runway.id];
    if (
      !routes ||
      routes.arrival.length === 0 ||
      routes.departure.length === 0
    ) {
      throw new Error(`Runway ${runway.id} is missing ground routes`);
    }
    for (const point of [...routes.arrival, ...routes.departure]) {
      if (!isFinitePoint(point)) {
        throw new Error(
          `Runway ${runway.id} has a non-finite ground route point`,
        );
      }
    }
  }

  for (const patch of map.scenery.grassPatches) {
    if (patch.points.length < 3) {
      throw new Error("Grass patches need at least three points");
    }
  }
  if (map.scenery.stream && map.scenery.stream.points.length < 2) {
    throw new Error("Stream needs at least two points");
  }

  return map;
}
